import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, Subject, defer, of } from 'rxjs';
import { catchError, concatMap, defaultIfEmpty, map, tap } from 'rxjs/operators';
import { BaseService } from '../base.service';
import { NodeHelper } from './node.helper';
import { EdgeFactory } from '../factories/edge.factory';
import { WorkspaceStore } from '../stores/workspace.store';
import { INodeInstanceModel } from '../../models/node/node-instance.model';
import { IEdgeFactoryPayload } from '../../models/edge/patch-edge-payload.model';
import { IWorkspaceInstanceModel } from '../../models/workspace/workspace-instance.model';
import { IntelliCatchTestCreateOutcome } from '../../models/copilot/intellicatch-create.enum';
import { IIntelliCatchTestCreateRequest, IIntelliCatchTestCreateResult } from '../../models/copilot/intellicatch-create.model';
import { planChildLocation } from '../../modules/smart-map/helpers/map-child-placement';
import * as BluePrintConstants from '../../modules/catch-core/data/constants/blueprint.constants';
import * as DefaultValueConstants from '../../modules/catch-core/data/constants/default-value.constants';
import * as UiMessageConstants from '../../modules/catch-core/data/constants/ui-message.constants';

interface IQueueEntry {
  request: IIntelliCatchTestCreateRequest;
  result: ReplaySubject<IIntelliCatchTestCreateResult>;
}

/**
 * Creates each suggested Test and links it under its source item, one at a time.
 *
 * `NodeHelper.createNodeWithFieldValues` unsubscribes the previous node PATCH on every call, so a
 * second create dropped the first one's response and the link made in it. `concatMap` runs one
 * create + link at a time, on subscriptions owned by this service alone.
 */
@Injectable({
  providedIn: 'root',
})
export class IntelliCatchLinkedTestHelper extends BaseService {
  private readonly queue = new Subject<IQueueEntry>();

  constructor(
    private nodeHelper: NodeHelper,
    private edgeFactory: EdgeFactory,
    private workspaceStore: WorkspaceStore,
  ) {
    super();
    // The error handler is the queue's last line of defence: `process()` settles every entry itself,
    // but if an error ever did reach here the subscription would be torn down and, this service
    // being root-scoped, every later create would silently go nowhere until a page reload.
    this.subscribers.intelliCatchCreateQueue = this.queue.pipe(concatMap((entry) => this.process(entry))).subscribe({
      error: (err) => console.error('IntelliCatch: the create queue failed.', err),
    });
  }

  /**
   * Queues one create and reports what became of it. The returned observable replays its single
   * result, so a caller that subscribes after a synchronous turn still receives it.
   */
  public queueCreate(request: IIntelliCatchTestCreateRequest | null | undefined): Observable<IIntelliCatchTestCreateResult> {
    const name = (request?.name ?? '').trim();
    const parentNodeId = (request?.parentNodeId ?? '').trim();
    if (!request || !name || !parentNodeId) {
      return of({
        name,
        outcome: IntelliCatchTestCreateOutcome.NotCreated,
        reason: name ? UiMessageConstants.INTELLICATCH_CREATE_REASON_PARENT_MISSING : UiMessageConstants.INTELLICATCH_CREATE_REASON_NO_NAME,
      });
    }

    const result = new ReplaySubject<IIntelliCatchTestCreateResult>(1);
    this.queue.next({ request: { ...request, name, parentNodeId }, result });
    return result.asObservable();
  }

  private process(entry: IQueueEntry): Observable<IIntelliCatchTestCreateResult> {
    const name = entry?.request?.name ?? '';
    // `defer`: the node payload is built synchronously when the PATCH method is called, and that
    // build throws on a missing workspace before any Observable exists - building it inside defer
    // turns the throw into an error notification this catchError can settle, instead of one that
    // tears the whole queue subscription down.
    return defer(() => this.createAndLink(entry?.request)).pipe(
      catchError((err) => {
        console.error('IntelliCatch: failed to create the suggested test.', err);
        return of({ name, outcome: IntelliCatchTestCreateOutcome.NotCreated, reason: UiMessageConstants.intelliCatchCreateErrorReason(err) });
      }),
      // A source that completes without emitting (a filtered-out empty PATCH body) must still
      // settle the entry, or its caller's count never completes and the batch is never reported.
      defaultIfEmpty({ name, outcome: IntelliCatchTestCreateOutcome.NotCreated, reason: UiMessageConstants.INTELLICATCH_CREATE_REASON_NOT_CREATED }),
      tap((result) => {
        entry?.result?.next(result);
        entry?.result?.complete();
      }),
    );
  }

  private createAndLink(request: IIntelliCatchTestCreateRequest | null | undefined): Observable<IIntelliCatchTestCreateResult> {
    const name = (request?.name ?? '').trim();
    if (!request || !name) {
      return of({ name, outcome: IntelliCatchTestCreateOutcome.NotCreated, reason: UiMessageConstants.INTELLICATCH_CREATE_REASON_NO_NAME });
    }

    // The workspace is captured with the parent, not read again when the create response arrives:
    // a label switch while the PATCH is in flight would otherwise link against whatever workspace
    // is selected by then. Checked here rather than once for the whole batch: the item can be
    // deleted between one create and the next, and an item created under a parent that has gone is
    // an orphan on the Map.
    const workspace = this.workspaceStore?.selectedWorkspaceInstance ?? null;
    const parentNode = this.findWorkspaceNode(this.getUnfilteredWorkspace(workspace), request.parentNodeId);
    if (!parentNode) {
      return of({ name, outcome: IntelliCatchTestCreateOutcome.ParentMissing, reason: UiMessageConstants.INTELLICATCH_CREATE_REASON_PARENT_MISSING });
    }

    // Placed at a random point near its parent rather than left for the map to drop below everything,
    // where each Test of a batch would land under the previous one. Read from the unfiltered
    // workspace so a node hidden by the active filter still counts as occupying its spot.
    const location =
      planChildLocation(
        parentNode.location,
        (this.getUnfilteredWorkspace(workspace)?.nodeInstances ?? []).map((node) => node?.location),
      ) ?? DefaultValueConstants.NODE_SERVER_LOCATION;

    return this.nodeHelper
      .$createNodeWithFieldValues(
        BluePrintConstants.TEST_NODE_LOWER,
        name,
        location,
        request.description ?? '',
        request.fieldValues ?? {},
        parentNode.id,
      )
      .pipe(
        concatMap((createdNode) => {
          if (!createdNode?.id) {
            return of({ name, outcome: IntelliCatchTestCreateOutcome.NotCreated, reason: UiMessageConstants.INTELLICATCH_CREATE_REASON_NOT_CREATED });
          }
          return this.linkToParent(workspace, parentNode, createdNode).pipe(
            map((linkFailure) => {
              const outcome = linkFailure === null ? IntelliCatchTestCreateOutcome.CreatedAndLinked : IntelliCatchTestCreateOutcome.CreatedNotLinked;
              // The create has succeeded by now, so a panel that fails to open must not turn the
              // outcome into "not created" - the retry would duplicate the item.
              if (request.isOpenEditPanel) {
                try {
                  this.nodeHelper.openEditPanelForNode(createdNode);
                } catch (err) {
                  console.error('IntelliCatch: could not open the created test in the edit panel.', err);
                }
              }
              return { name, outcome, reason: linkFailure ?? '' };
            }),
          );
        }),
      );
  }

  /** Links the created Test under its parent. Emits `null` once it is linked, otherwise the reason it could not be. */
  private linkToParent(workspace: IWorkspaceInstanceModel | null, parentNode: INodeInstanceModel, createdNode: INodeInstanceModel): Observable<string | null> {
    // A workspace switch mid-create leaves the link with no workspace to belong to: the edge would
    // be patched against the newly selected one, a different label or blueprint entirely.
    if (!workspace?.id || this.workspaceStore?.selectedWorkspaceInstance?.id !== workspace.id) {
      return of(UiMessageConstants.INTELLICATCH_CREATE_REASON_LABEL_SWITCHED);
    }
    const edgePayload: IEdgeFactoryPayload = {
      selectedWorkspace: workspace,
      sourceNode: parentNode,
      targetNode: createdNode,
    };
    const patchEdgePayload = this.edgeFactory.createParentChildEdgePayload(edgePayload);
    if (!patchEdgePayload) {
      console.error('IntelliCatch: no parent-child relationship is defined between these item types.', {
        parentNodeTypeId: parentNode?.nodeTypeId,
        createdNodeTypeId: createdNode?.nodeTypeId,
      });
      return of(UiMessageConstants.INTELLICATCH_CREATE_REASON_NO_RELATIONSHIP);
    }

    return this.workspaceStore.$patchWSIEdge(patchEdgePayload).pipe(
      map((edgeRes): string | null => (edgeRes ? null : UiMessageConstants.INTELLICATCH_CREATE_REASON_LINK_NOT_CONFIRMED)),
      // The PATCH filters a null/empty body out rather than erroring, completing with no value.
      defaultIfEmpty<string | null, string | null>(UiMessageConstants.INTELLICATCH_CREATE_REASON_LINK_NOT_CONFIRMED),
      catchError((err) => {
        console.error('IntelliCatch: failed to link the created test to its item.', err);
        return of(UiMessageConstants.intelliCatchCreateErrorReason(err));
      }),
    );
  }

  // The selected workspace is the filtered clone, so an item the active filter hides is not in it.
  private getUnfilteredWorkspace(workspace: IWorkspaceInstanceModel | null): IWorkspaceInstanceModel | null {
    const unfiltered = this.workspaceStore?.rawWorkspaceInstance ?? null;
    return unfiltered && workspace?.id && unfiltered.id === workspace.id ? unfiltered : workspace;
  }

  private findWorkspaceNode(workspace: IWorkspaceInstanceModel | null | undefined, nodeId: string | null | undefined): INodeInstanceModel | null {
    if (!nodeId) {
      return null;
    }
    const nodeInstances = workspace?.nodeInstances;
    if (!Array.isArray(nodeInstances)) {
      return null;
    }
    return nodeInstances.find((node) => node?.id === nodeId) ?? null;
  }
}
